// Etiquetas display de los campos de items de propuesta v2.
// La BD almacena `tipo_frecuencia` como "MES" o "AÑO" (solo ítems PLAN);
// para items TAREA es null y representan trabajos puntuales.

export const FRECUENCIA_PLAN_LABEL = {
  MES: "Mensual",
  AÑO: "Anual",
};

/**
 * Devuelve el caption descriptivo del tipo + frecuencia de un item:
 *   - Item PLAN con tipoFrecuencia conocida -> "Plan Mensual" / "Plan Anual"
 *   - Item PLAN sin tipoFrecuencia (data anomaly) -> "Plan"
 *   - Item TAREA -> "Tarea Única"
 *
 * El item puede pasarse en su shape camelCase (frontend RTK Query) o
 * snake_case (backend resolver / mapper). Acepta ambos.
 */
export function formatearCaptionItem(item) {
  if (!item) return "";
  const tipo = item.tipoItem ?? item.tipo_item;
  if (tipo !== "PLAN") return "Tarea Única";
  const frecuencia = item.tipoFrecuencia ?? item.tipo_frecuencia;
  const label = FRECUENCIA_PLAN_LABEL[frecuencia];
  return label ? `Plan ${label}` : "Plan";
}

// Nombres exactos del catálogo tipoPlanes que se cobran por trabajador y no a
// tarifa fija. tipoPlanes no tiene una columna que diga cómo se cobra un plan,
// y la categoría tampoco sirve para deducirlo: REMUNERACIONES agrupa estos dos
// planes junto con otros de tarifa fija. Por eso la lista queda fija acá hasta
// que el catálogo declare la modalidad de cobro de cada plan. Gemela de la del
// backend (utils/propuestas.js) — mantener ambas sincronizadas.
const PLANES_COBRO_POR_TRABAJADOR = [
  "ASESORIA LABORAL Y REMUNERACIONES",
  "PLAN REMUNERACIONES",
];

// Normaliza el nombre del plan (recorta espacios, quita tildes, mayúsculas)
// antes de compararlo contra la lista fija de arriba, para que una variación
// de escritura del mismo nombre no quede afuera.
export function esCobroPorTrabajador(refTipoPlan) {
  if (!refTipoPlan) return false;
  const normalizado = String(refTipoPlan)
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  return PLANES_COBRO_POR_TRABAJADOR.includes(normalizado);
}

// Metadata de presentación de la modalidad de cobro. Gemela de la del backend
// (utils/propuestas.js) — mantener ambas sincronizadas.
export const MODALIDAD_META = {
  MENSUAL: {
    inline: "al mes",
    subtotal: "Mensual",
    grupo: "RECURRENTE",
    unidad: "/ mes",
  },
  MENSUAL_POR_TRABAJADOR: {
    inline: "por trabajador al mes",
    subtotal: "Mensual por trabajador",
    grupo: "RECURRENTE",
    unidad: "/ trabajador / mes",
  },
  ANUAL: {
    inline: "al año",
    subtotal: "Anual",
    grupo: "RECURRENTE",
    unidad: "/ año",
  },
  ANUAL_POR_TRABAJADOR: {
    inline: "por trabajador al año",
    subtotal: "Anual por trabajador",
    grupo: "RECURRENTE",
    unidad: "/ trabajador / año",
  },
  RECURRENTE: {
    inline: "",
    subtotal: "Recurrente",
    grupo: "RECURRENTE",
    unidad: "",
  },
  UNICO: {
    inline: "pago único",
    subtotal: "Pago único",
    grupo: "UNICO",
    unidad: "",
  },
};

// Resuelve la clave de modalidad de un item (acepta shape camelCase o snake_case).
export function modalidadItem(item) {
  if (!item) return "UNICO";
  const tipo = item.tipoItem ?? item.tipo_item;
  const frecuencia = item.tipoFrecuencia ?? item.tipo_frecuencia;
  const refTipoPlan = item.refTipoPlan ?? item.ref_tipo_plan;
  const porTrabajador = tipo === "PLAN" && esCobroPorTrabajador(refTipoPlan);
  if (porTrabajador && frecuencia === "MES") return "MENSUAL_POR_TRABAJADOR";
  if (porTrabajador && frecuencia === "AÑO") return "ANUAL_POR_TRABAJADOR";
  if (tipo === "PLAN" && frecuencia === "MES") return "MENSUAL";
  if (tipo === "PLAN" && frecuencia === "AÑO") return "ANUAL";
  if (tipo === "PLAN") return "RECURRENTE";
  return "UNICO";
}

// Orden de presentación de las modalidades de cobro. Gemela de la del backend
// (utils/propuestas.js) — mantener ambas sincronizadas.
export const ORDEN_MODALIDAD = [
  "MENSUAL",
  "MENSUAL_POR_TRABAJADOR",
  "ANUAL",
  "ANUAL_POR_TRABAJADOR",
  "RECURRENTE",
  "UNICO",
];

/**
 * Clave de agrupación por empresa de una fase ya mapeada (shape camelCase).
 * Es el punto único que define qué items pertenecen a la misma empresa: la
 * numeración y el render del PDF DEBEN usar esta misma clave, o la numeración
 * deja de ser contigua dentro de cada grupo renderizado. Gemela de la del
 * backend (utils/propuestas.js) — mantener ambas sincronizadas.
 *
 * Manda el RUT y no el nombre porque el RUT es la identidad del cliente y el
 * nombre una etiqueta que se repite: dos sociedades distintas con la misma
 * razón social caerían en un bloque único con los subtotales sumados, o sea un
 * monto equivocado en el documento que el cliente firma. Los dos campos se
 * escriben siempre juntos, así que anteponer el RUT no deja items sin agrupar.
 */
export function claveEmpresaFase(fase) {
  return fase?.refCliente || fase?.nombreCliente || "";
}
